import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import type { Message, Session } from 'talkjs/all';
import { selectIsUserParticipant } from '@rsos/reduxDataStore/src/features/audioNotifications/textMessageNotifications';
import { setSelectedCallerID } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import {
  expand,
  setSelectedTab,
} from '@rsos/reduxDataStore/src/features/eventPane/eventPaneSlice';
import { CHAT_TAB } from '@rsos/reduxDataStore/src/features/eventPane/eventPaneTabs';
import { selectCallerIDForConversationID } from '@rsos/reduxDataStore/src/features/selectors/conversationCallerSelector';
import { selectIsFollowUpReplyBannerEnabled } from '@rsos/sinatra/src/selectors/selectFollowUpReplyNotifications';
import { dismissToastByID, notifyInboundReply } from '@rsos/utils/toastUtils';
import type { RootState } from '../app';

const toastIdFor = (callerID: string): string =>
  `inbound-reply-toast-${callerID}`;

interface UseInboundReplyToastNotificationsArgs {
  /** Narrowed so tests can pass a plain object, not a whole Session. */
  session: Pick<Session, 'onMessage'> | null;
}

/**
 * Banners a caller's reply on any text stream, or a position coming back from a
 * location request.
 */
const useInboundReplyToastNotifications = ({
  session,
}: UseInboundReplyToastNotificationsArgs): void => {
  const dispatch = useDispatch();
  // The message handler reads the conversation slices through getState(). Via
  // useSelector it would be rebuilt, and resubscribe, on every message.
  const store = useStore<RootState>();

  const selectedCallerID = useSelector(
    (state: RootState) => state.emergencyCalls.selectedCallerID,
  );
  // A primitive key: avoids re-rendering on unrelated queue updates.
  const callersWithLocationReply = useSelector((state: RootState) =>
    Object.entries(state.emergencyCalls.callQueue ?? {})
      .filter(([, call]) => call?.ui?.requestedLocationReceived)
      .map(([callerID]) => callerID)
      .join(','),
  );

  const openCall = useCallback(
    (callerID: string) => {
      dispatch(setSelectedCallerID(callerID));
      dispatch(setSelectedTab(CHAT_TAB));
      dispatch(expand());
    },
    [dispatch],
  );

  const shouldNotify = useCallback(
    (callerID: string) => {
      const state = store.getState();
      const isParticipant = selectIsUserParticipant(state, callerID);
      if (!selectIsFollowUpReplyBannerEnabled(state, isParticipant)) {
        return false;
      }
      if (!state.emergencyCalls.callQueue?.[callerID]) return false;
      // Already reading this call — the reply lands in front of the operator.
      return !(
        state.emergencyCalls.selectedCallerID === callerID &&
        state.eventPane.selectedTab === CHAT_TAB
      );
    },
    [store],
  );

  const notify = useCallback(
    (callerID: string, variant: 'text' | 'location') => {
      notifyInboundReply({
        callerID,
        variant,
        onOpen: () => openCall(callerID),
        options: { toastId: toastIdFor(callerID) },
      });
    },
    [openCall],
  );

  useEffect(() => {
    if (!session) return;
    const subscription = session.onMessage((message: Message) => {
      if (message.isByMe) return;
      const callerID = selectCallerIDForConversationID(
        store.getState(),
        message.conversation.id,
      );
      if (!callerID || !shouldNotify(callerID)) return;
      notify(callerID, 'text');
    });
    return () => subscription.unsubscribe();
  }, [session, store, shouldNotify, notify]);

  // A location reply is a position, not a message, so it never reaches TalkJS.
  useEffect(() => {
    if (!callersWithLocationReply) return;
    callersWithLocationReply.split(',').forEach(callerID => {
      if (!shouldNotify(callerID)) return;
      notify(callerID, 'location');
    });
    // The flag stays set until the Chat tab opens, which also dismisses the
    // banner, so re-running on an unchanged key would re-raise it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callersWithLocationReply]);

  // Opened by any means — the banner, the queue row, or a programmatic select.
  useEffect(() => {
    if (selectedCallerID) {
      dismissToastByID(toastIdFor(selectedCallerID));
    }
  }, [selectedCallerID]);
};

export default useInboundReplyToastNotifications;
